import { finalize } from "rxjs/operators";
import { HttpResponse } from "@angular/common/http";
import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { AuditUniverseDataService } from "@app/audit-universe/services/audit-universe-data.service";
import { AuditUniverseModel } from "@app/audit-universe/shared/data-models/audit-universe-model";
import { AuditUniverseDetailPermissionModel } from "@app/audit-universe/shared/page-models/audit-universe-detail-permission-model";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { Pages } from "@constants/pages/Pages";
import { TabModel } from "@models/generic/TabModels/TabModel";
import moment from "moment";
import { ToastrService } from "ngx-toastr";
import { TabMenuItem } from "./../../../../common/models/generic/TabModels/TabMenuItem";
import { Configuration } from "./../../../app.constants";
import { CerrixPromptService } from "./../../../shared/services/cerrix-prompt.service";
import { AuditUniverseDetailStandingDataModel } from "./../../shared/page-models/audit-universe-detail-standing-data-model";
import { AuditUniverseDataComponent } from "./components/audit-universe-data/audit-universe-data.component";
import { DocumentModel } from "@models/documents/documentmodel";
import { isGuid } from "@methods/uniqueMethods";
import { TabComponentHelper } from "@app/shared/helpers/tab-component-helper";
import { ActivatedRoute } from "@angular/router";
import { TargetModule } from "@enums/document/TargetModule";
import { AuditUniverseLinkedAuditsComponent } from "./components/audit-universe-linked-audits/audit-universe-linked-audits.component";
import { AuditUniverseLinkedFormsComponent } from "./components/audit-universe-linked-forms/audit-universe-linked-forms.component";
import { HistoryOverviewComponent } from "@app/shared/history-overview/history-overview.component";
import { firstValueFrom } from "rxjs";
import { AuditUniverseDetailMainComponent } from "./components/audit-universe-detail-main/audit-universe-detail-main.component";
import { SharedModule } from "@app/shared/shared.module";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { DateInputComponent, DateFormatPipe, DateTimeFormatPipe } from "@app/shared";
import { DetailOptionsComponent } from "@app/shared/components/detail-options/detail-options.component";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/audit-universe/i18n/scope";

@Component({
    selector: "audit-universe-detail",
    templateUrl: "./audit-universe-detail.component.html",
    styleUrls: ["./audit-universe-detail.component.scss"],
    imports: [
        AuditUniverseDetailMainComponent,
        AuditUniverseDataComponent,
        AuditUniverseLinkedAuditsComponent,
        AuditUniverseLinkedFormsComponent,
        HistoryOverviewComponent,
        CerrixLoaderComponent,
        MenuSystemDirective,
        DateInputComponent,
        DateFormatPipe,
        DateTimeFormatPipe,
        DetailOptionsComponent,
        // Only DocumentManagerComponent is pulled from here; it's still a classic
        // NgModule-declared component with no standalone alternative yet.
        SharedModule,
        TranslatePipe,
    ],
    viewProviders: [provideTranslationScope(scope)],
})
export class AuditUniverseDetailComponent implements OnInit {
    private route = inject(ActivatedRoute);
    _ds = inject(AuditUniverseDataService);
    private _promptService = inject(CerrixPromptService);
    private _configuration = inject(Configuration);
    private _pages = inject(Pages);
    private _toastr = inject(ToastrService);
    private _translation = injectTranslation(scope);

    tab: TabModel;
    id: string;

    historicalView = false;
    readonly = false;

    permissions: AuditUniverseDetailPermissionModel;
    standingData: AuditUniverseDetailStandingDataModel;
    model: AuditUniverseModel;
    formGroup: FormGroup;

    @ViewChild("auditsComponent")
    auditsComponent: AuditUniverseLinkedAuditsComponent;
    protected menuItemAudits: string = "menuItemAudits";

    @ViewChild("formsComponent")
    formsComponent: AuditUniverseLinkedFormsComponent;
    protected menuItemForms: string = "menuItemForms";

    @ViewChild("historyOverviewComponent") historyOverviewComponent: HistoryOverviewComponent;
    protected menuItemHistory: string = "menuItemHistory";

    public static readonly menuItemData = "audit-universe-data";
    get menuItemData() {
        // We cant use the static variable in html so we create a get variable here to pass through the value.
        return AuditUniverseDetailComponent.menuItemData;
    }

    public static readonly menuItemDocuments = "audit-universe-documents";
    get menuItemDocuments() {
        // We cant use the static variable in html so we create a get variable here to pass through the value.
        return AuditUniverseDetailComponent.menuItemDocuments;
    }

    get _loadedDateFilter() {
        return this.tab.config && this.tab.config.datefilter
            ? moment.utc(this.tab.config.datefilter, "YYYYMMDD").toDate()
            : moment.utc().toDate();
    }
    set _loadedDateFilter(date: Date) {
        if (!this.tab.config) {
            this.tab.config = {};
        }

        const isToday = moment.utc().isSame(date, "day");
        if (isToday) {
            delete this.tab.config.datefilter;
        } else {
            this.tab.config.datefilter = moment.utc(date).format("YYYYMMDD");
        }
    }
    dateFilter: Date;
    dateFilterMax: Date;

    documents: DocumentModel[];
    isDocumentDirty = false;
    targetModule = TargetModule.AuditUniverse;

    @ViewChild(AuditUniverseDataComponent) moduleDataComp: AuditUniverseDataComponent;

    constructor() {
        this.dateFilterMax = moment.utc().toDate();
    }

    async ngOnInit() {
        // The template (and its `transloco` pipes) stays hidden behind the loader until the
        // object has loaded, but the toasts and tab name below are translated before that.
        await this._translation.ready();

        this.id = this.route.snapshot.params.id ? this.route.snapshot.params.id : this.tab.id;
        this.permissions = await firstValueFrom(this._ds.getPermissions(this.id));
        if (!this.permissions.canView) {
            this._toastr.error(this._translation.t("detail.noAccess"));
            this.tab.close(false);
            return;
        }

        this.model = this.id
            ? await firstValueFrom(
                  this._ds.getObject(
                      this.id,
                      this.permissions.canChangeDate ? this._loadedDateFilter : null
                  )
              )
            : new AuditUniverseModel();

        this.standingData = await firstValueFrom(
            this._ds.getStandingData(
                this.id,
                this.permissions.canChangeDate ? this._loadedDateFilter : null
            )
        );

        this.dateFilterMax = moment.utc().toDate();
        this.createFormGroup();

        this.tab.name =
            "(UO) " +
            (this.model.name
                ? +this.model.identifier + " - " + this.model.name
                : this._translation.t("detail.newUniverseObject"));
        this.tab.showLoader = false;
    }

    async save() {
        const canSave = this.id ? this.permissions.canEdit : this.permissions.canCreate;
        if (!canSave) {
            this._toastr.warning(
                this._translation.t("detail.notEnoughRightsToSave"),
                this._translation.t("detail.unauthorized")
            );
            return;
        }

        if (this.formGroup.valid) {
            const savingPrompt = this._promptService.loader(
                this._translation.t("detail.savingChanges")
            );

            this._ds
                .storeObject(this.model)
                .pipe(finalize(() => savingPrompt.close()))
                .subscribe({
                    next: (value: HttpResponse<any>) => {
                        if (!value.ok || value.status != 200) {
                            return;
                        }

                        savingPrompt.close();
                        this._toastr.success(
                            "",
                            this._translation.t("detail.saveCompleted")
                        );
                        this.tab.id = value.body.toString();
                        if (
                            this.tab.parent &&
                            this.tab.parent.lookupname === this._pages.AuditUniverseOverview
                        ) {
                            this.tab.parent.refresh();
                        }
                        this.tab.refresh();
                    },
                });
        } else {
            FormValidationHelper.markAllAsTouched(this.formGroup);

            const validationErrors = FormValidationHelper.getFormControlErrors(this.formGroup);
            const validationMessage = FormValidationHelper.getGeneralErrorMessage(validationErrors);
            this._toastr.warning(
                validationMessage,
                this._translation.t("detail.saveFailed"),
                { enableHtml: true }
            );
        }
    }

    delete() {
        this._ds.deleteObject(this.model.guid).subscribe({
            next: (value) => {
                if (value && value.length > 0) {
                    this._toastr.warning(value.toString());
                } else {
                    this._toastr.success(
                        this._translation.t("detail.auditUniverseDeleted")
                    );
                    if (
                        this.tab.parent &&
                        this.tab.parent.lookupname === this._pages.AuditUniverseOverview
                    ) {
                        this.tab.parent.refresh();
                    }
                    this.tab.close(false);
                }
            },
            error: (error) => {
                this._toastr.error("", error || error.Message);
            },
        });
    }

    handleMenuItemClick(menuItem: TabMenuItem) {
        if (!menuItem) {
            return;
        }

        switch (menuItem.menuItemId) {
            case this.menuItemData:
                if (this.moduleDataComp) {
                    this.moduleDataComp.load();
                }
                break;
            case this.menuItemAudits:
                this.auditsComponent.load();
                break;
            case this.menuItemForms:
                this.formsComponent.load();
                break;
            case this.menuItemDocuments:
                this.loadDocuments();
                break;
            case this.menuItemHistory:
                this.historyOverviewComponent.loadHistory();
                break;
        }
    }

    private createFormGroup() {
        this.formGroup = new FormGroup({
            name: new FormControl(this.model.name, [
                Validators.required,
                FormValidationHelper.validateMaxLength(
                    "Name",
                    this._configuration.MaxTextFieldLength
                ),
            ]),
            description: new FormControl(
                this.model.description,
                FormValidationHelper.validateMaxLength(
                    "Description",
                    this._configuration.MaxTextAreaLength
                )
            ),

            impact: new FormControl(this.model.impact),
            impactComment: new FormControl(
                this.model.impactComment,
                FormValidationHelper.validateMaxLength(
                    "Impact comment",
                    this._configuration.MaxTextAreaLength
                )
            ),

            organizations: new FormControl(this.model.organizations),
            businessDimensions: new FormControl(this.model.businessDimensions),
            frameworkDimensions: new FormControl(this.model.frameworkDimensions),
        });

        const hasRights = this.id ? this.permissions.canEdit : this.permissions.canCreate;
        this.historicalView =
            this._loadedDateFilter && !moment.utc().isSame(this._loadedDateFilter, "day");
        this.readonly = !hasRights || this.historicalView;

        if (this.readonly) {
            this.formGroup.disable();
        } else {
            this.formGroup.valueChanges.subscribe(() => {
                FormValidationHelper.mapToModel(this.formGroup, this.model);
            });
        }

        FormValidationHelper.mapToModel(this.formGroup, this.model);
    }

    async dateChanged() {
        // Date has not changed.
        if (moment.utc(this.dateFilter).isSame(this._loadedDateFilter, "day")) {
            return;
        }

        const isToday = moment.utc().isSame(this.dateFilter, "day");
        if (!isToday) {
            const result = await this._promptService
                .confirmCustom({
                    maxWidth: "375px",
                    data: {
                        title: this._translation.t(
                            "detail.loadHistoricalDataTitle"
                        ),
                        message: this._translation.t(
                            "detail.loadHistoricalDataMessage"
                        ),
                    },
                })
                .toPromise();

            if (!result) {
                this.dateFilter = this._loadedDateFilter;
                return;
            }
        }

        this._loadedDateFilter = this.dateFilter;

        this.tab.showLoader = true;
        this.tab.refresh();
    }

    loadDocuments(): void {
        if (!this.documents) {
            this.documents = [];
        }

        if (this.id && isGuid(this.id.toString())) {
            this._ds.getDocuments(this.id.toString()).subscribe((documents) => {
                this.documents = documents;
                this.model.Documents = documents;
            });
        }
    }

    checkDocumentsDirty(): void {
        TabComponentHelper.toggleTabDirty(this.tab, this.menuItemDocuments, true);
        this.isDocumentDirty = true;
    }
}
